import {initializePaddle, Paddle} from "@paddle/paddle-js";
import {useCallback, useEffect, useState} from "react";
import {PADDLE_ENV, paddleClientToken} from "@/app/lib/paddle/setup";
import * as Sentry from "@sentry/nextjs";
import {PaddleEventData} from "@paddle/paddle-js/types/checkout/checkout";

const DEBUG = false;

export function usePaddle(eventCallback?: (event: PaddleEventData) => void) {
    const [paddle, setPaddle] = useState<Paddle | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | undefined>(undefined);

    const onPaddleEvent = useCallback((event: PaddleEventData) => {
        if (DEBUG) {
            console.log('Paddle event', event);
        }
        eventCallback?.(event);
    }, [eventCallback]);

    // Download and initialize Paddle instance from CDN
    useEffect(() => {
        initializePaddle({
            environment: PADDLE_ENV,
            token: paddleClientToken(),
            checkout: {
                settings: {
                    showAddTaxId: true,
                    allowDiscountRemoval: false,
                    allowLogout: false,
                    displayMode: 'overlay',
                    showAddDiscounts: true,
                },
            },
            debug: DEBUG,
            eventCallback: onPaddleEvent
        }).then(
            (paddleInstance: Paddle | undefined) => {
                if (paddleInstance != null) {
                    setPaddle(paddleInstance);
                } else {
                    throw new Error('Failed to initialize Paddle');
                }
            },
        ).catch((error: Error) => {
            Sentry.captureException(error);
            console.error(error);
            setError(error);
        }).finally(() => {
            setLoading(false);
        })
    }, [onPaddleEvent]);

    return {loading, paddle, error};
}
